/**
 * The body of a browser-test evidence row: what it checked, then "How it was checked":
 * the run's status and video with the agent's plain-sentence steps beside it (older
 * evidence without steps shows the test's flow as markdown instead). For a screen check
 * it also shows the base-versus-head visual checkpoints.
 *
 * @module features/proofs/components/E2eEvidenceBody
 */
import React from 'react';
import { Markdown, StatusBadge, Text, normalizeRunStatus } from '@chatsdet/ui';
import { useWorkflowSteps } from '../hooks/useWorkflowSteps';
import type { ProofEvidence } from '../types';
import { EvidenceVideo } from './EvidenceVideo';
import { UiTestCheckpoints } from './UiTestCheckpoints';

type E2e = Extract<ProofEvidence, { kind: 'e2e' }>;

// Fallback for evidence attached without --steps (older CLIs, or the flag omitted). Remove only if steps become required for browser tests.
const FlowMarkdown: React.FC<{ workflowId: string }> = ({ workflowId }) => {
  const flow = useWorkflowSteps(workflowId, true);
  if (flow.isError) return <Text color="muted">{flow.error.message}</Text>;
  if (!flow.data) return <Text color="muted">Loading steps…</Text>;
  return <Markdown className="prose prose-sm max-w-none">{flow.data.markdown}</Markdown>;
};

export const E2eEvidenceBody: React.FC<{ item: E2e; expanded: boolean }> = ({ item, expanded }) => {
  const { run, steps } = item;
  // The API stores null or a non-empty list (older evidence is null).
  const hasSteps = steps !== null;
  const media = run ? (
    <div className={`min-w-0 space-y-2${hasSteps ? ' lg:w-3/5' : ''}`} data-testid="evidence-media">
      <StatusBadge status={normalizeRunStatus(run.status)} size="sm" />
      <EvidenceVideo run={run} nodeId={item.nodeId} />
    </div>
  ) : (
    <Text color="muted">This run has expired, so there is no video.</Text>
  );
  return (
    <div className="space-y-4">
      <Text size="sm">
        <span className="font-semibold">What it checked:</span> {item.asserted}
      </Text>
      <Text weight="semibold">How it was checked</Text>
      {hasSteps ? (
        <div className="flex flex-col gap-4 lg:flex-row" data-testid="evidence-steps-layout">
          {media}
          <ol className="min-w-0 list-decimal space-y-1 pl-5 text-sm lg:w-2/5" data-testid="evidence-steps">
            {steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </div>
      ) : (
        <>
          {media}
          {expanded && <FlowMarkdown workflowId={item.workflowId} />}
        </>
      )}
      {item.ui && <UiTestCheckpoints ui={item.ui} />}
    </div>
  );
};
